import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { getErpUserId } from '../auth/erp-auth-token.util';
import { DocumentService } from '../document/document.service';
import { appendErpUserStationParams } from '../erp-api-query.helper';
import { LogicielCaisseDashboardStatsService } from '../logiciel-caisse/logiciel-caisse-dashboard-stats.service';
import {
  LogicielCaisseTicketService,
  TicketsCounts,
} from '../logiciel-caisse/logiciel-caisse-ticket.service';

export interface ErpVenteDashboardPayload {
  success: boolean;
  filters: {
    station_id: number;
    date_from: string;
    date_to: string;
  };
  kpis: {
    devis: { period: number; pending: number };
    commande_client: { period: number; pending: number };
    bon_livraison: { period: number; pending: number };
    facture: {
      period: number;
      pending_non_payee: number;
      pending_brouillon: number;
    };
    bon_sortie: { period: number; pending: number };
    avoir: { period: number; pending: number };
    pending_actions_total: number;
  };
  series: {
    devis: { day: string; count: number }[];
    commande_client: { day: string; count: number }[];
    bon_livraison: { day: string; count: number }[];
    facture: { day: string; count: number }[];
    bon_sortie: { day: string; count: number }[];
    avoir: { day: string; count: number }[];
  };
  error?: string;
}

export interface FacturesClientGlobalesStats {
  total_ht: number;
  total_ttc: number;
  factures_payees: { nombre: number; total_ttc: number };
  factures_a_payer: {
    nombre: number;
    total_ttc: number;
    detail: {
      factures_non_payees: { nombre: number; total_ttc: number };
      montant_restant_factures_partiellement_payees: {
        nombre: number;
        montant_restant: number;
      };
    };
  };
  factures_partiellement_payees?: {
    nombre: number;
    total_ttc: number;
    montant_restant: number;
  };
  total_factures: number;
}

export interface VenteLcDashboardSlice {
  ventesPeriode: { nb_documents: number; ca_ttc: number } | null;
  tickets: TicketsCounts | null;
}

export interface VenteDashboardBundle {
  erp: ErpVenteDashboardPayload | null;
  facturesGlobales: FacturesClientGlobalesStats | null;
  lc: VenteLcDashboardSlice | null;
}

@Injectable({ providedIn: 'root' })
export class VenteDashboardService {
  private readonly erpUrl = `${String(environment.document_url || environment.apiUrl).replace(
    /\/$/,
    ''
  )}/api/documents/vente/dashboard-stats`;

  constructor(
    private readonly http: HttpClient,
    private readonly documentService: DocumentService,
    private readonly lcDashboard: LogicielCaisseDashboardStatsService,
    private readonly lcTicketService: LogicielCaisseTicketService
  ) {}

  fetchErpStats(params: {
    stationId: number;
    dateFrom: string;
    dateTo: string;
  }): Observable<ErpVenteDashboardPayload> {
    let hp = new HttpParams()
      .set('date_from', params.dateFrom)
      .set('date_to', params.dateTo);
    hp = appendErpUserStationParams(hp, { stationId: params.stationId });
    return this.http.get<ErpVenteDashboardPayload>(this.erpUrl, { params: hp }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Statistiques ERP indisponibles');
        }
        return res;
      }),
      catchError((err) =>
        throwError(
          () =>
            new Error(
              (err?.error?.error as string) ||
                err?.message ||
                'Erreur chargement statistiques vente'
            )
        )
      )
    );
  }

  fetchBundle(params: {
    stationId: number;
    dateFrom: string;
    dateTo: string;
    includeLc: boolean;
  }): Observable<VenteDashboardBundle> {
    const erp$ = this.fetchErpStats({
      stationId: params.stationId,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    }).pipe(catchError(() => of(null)));

    const userId = getErpUserId() ?? undefined;
    const factures$ = this.documentService
      .getStatsFacturesGlobales(params.stationId, userId)
      .pipe(
        map((res) => (res?.success ? res.data ?? null : null)),
        catchError(() => of(null))
      );

    if (!params.includeLc) {
      return forkJoin({
        erp: erp$,
        facturesGlobales: factures$,
        lc: of(null),
      });
    }

    const lcStats$ = this.lcDashboard
      .fetchStats({
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        entrepotId: 'all',
      })
      .pipe(
        map((res) => ({
          nb_documents: res?.ventes?.kpis?.nb_documents ?? 0,
          ca_ttc: res?.ventes?.kpis?.ca_ttc ?? 0,
        })),
        catchError(() => of(null))
      );

    const tickets$ = this.lcTicketService.getTicketsCounts().pipe(catchError(() => of(null)));

    const lc$ = forkJoin({ ventesPeriode: lcStats$, tickets: tickets$ }).pipe(
      map(({ ventesPeriode, tickets }) => ({
        ventesPeriode,
        tickets,
      }))
    );

    return forkJoin({ erp: erp$, facturesGlobales: factures$, lc: lc$ });
  }
}
